'use client';

import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/**
 * House defaults for client reads. Per-query overrides belong on the surface
 * that can justify them.
 *
 * - `refetchOnWindowFocus: false` — avoid refetching every surface on focus.
 * - `retry: 1` — retry one transient failure without stalling a real outage.
 * - `staleTime: 0` — queries are stale until a surface opts into freshness.
 *
 * Lists additionally pass `placeholderData: keepPreviousData` per query — see
 * the client-data-fetching skill.
 */
function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 0,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

/**
 * The browser's QueryClient, reachable from plain modules that run outside
 * React — `lib/applicant/cache.ts` invalidates from a mutation callback, not
 * from a component, so it cannot use `useQueryClient()`.
 *
 * On the server this returns a **fresh** client every call: during SSR of the
 * shell a shared client would leak one request's data into another's render.
 * Hire Go does not dehydrate/hydrate. The org Applicants page can pass one
 * request-scoped server read as initialData for its exact URL instead; a
 * server client still exists only for the duration of one render.
 */
export function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    return makeQueryClient();
  }
  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  // Created in the initializer so React owns it for the life of this tree, and
  // so an SSR render never reuses a client across requests. In the browser
  // `getQueryClient` hands back the module singleton, so this tree and
  // `getQueryClient()` callers share one cache.
  const [queryClient] = useState(getQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}
